import { ExtraBlock, SectionKey } from '../schema/site'

/**
 * The form's list of cards, in page order: every section of the template,
 * and after each the owner's blocks anchored to it. Sections and blocks share
 * one list so a block can be dragged between sections, and the ids say which
 * is which.
 */
export const sectionItemId = (key: SectionKey) => `section:${key}`
export const extraItemId = (id: string) => `extra:${id}`

export const extraIdOfItem = (item: string): string | undefined =>
  item.startsWith('extra:') ? item.slice('extra:'.length) : undefined

const sectionKeyOfItem = (item: string): SectionKey | undefined =>
  item.startsWith('section:')
    ? (item.slice('section:'.length) as SectionKey)
    : undefined

export function listItems(
  order: readonly SectionKey[],
  extras: ExtraBlock[]
): string[] {
  const items: string[] = []
  order.forEach(key => {
    items.push(sectionItemId(key))
    extras
      .filter(extra => extra.after === key)
      .forEach(extra => items.push(extraItemId(extra.id)))
  })
  // A block anchored to a section this template lacks is listed last, where
  // the page puts it.
  extras
    .filter(extra => !order.includes(extra.after))
    .forEach(extra => items.push(extraItemId(extra.id)))
  return items
}

/**
 * The blocks as the list now reads, after a drag.
 *
 * Each block's anchor becomes the section above it in the list, and the
 * blocks keep the order they are listed in. A block dragged above the first
 * section has nowhere to be anchored but that section, so it lands after it:
 * nothing goes before the top of the page.
 */
export function reanchorExtras(
  items: readonly string[],
  extras: ExtraBlock[],
  order: readonly SectionKey[]
): ExtraBlock[] {
  const byId = new Map(extras.map(extra => [extra.id, extra]))
  let anchor: SectionKey = order[0]
  const next: ExtraBlock[] = []

  items.forEach(item => {
    const key = sectionKeyOfItem(item)
    if (key) {
      anchor = key
      return
    }
    const id = extraIdOfItem(item)
    const extra = id ? byId.get(id) : undefined
    if (extra) next.push({ ...extra, after: anchor })
  })

  return next
}
